import { Component, OnInit, ViewChild } from "@angular/core";
import { firstValueFrom } from 'rxjs';
import { MatTableDataSource } from "@angular/material/table";
import { UserType } from "src/app/models/directory/enums/UserType.enum";
import { IDirectoryUser } from "src/app/models/directory/users/DirectoryUser";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { IWorkflowRequest } from "src/app/models/requests/WorkflowRequest";
import { PageLayout } from "@cvx/nextpage";
import { ResultSizeConstants } from "src/app/models/common/enums/ResultSizeConstants.enum";
import { GraphUserService } from "src/app/services/graph/GraphUser.service";
import { CalAngularService } from "@cvx/cal-angular";
import { IGenericApiResponseWithWorkflowRequest } from "src/app/models/common/GenericApiResponseWithWorkflowRequest";
import { DeferServiceAccountRequest } from "src/app/models/servicePrivilegeAccounts/DeferServiceAccountRequest";
import { DialogAppSearchChevronComponent } from "src/app/components/_shared/dialog-app-search-chevron/dialog-app-search-chevron.component";
import { IDialogAppSearchChevronData } from "src/app/models/idm/applications/IDialogAppSearchChevronData";
import { IIdmApplication } from "src/app/models/idm/applications/IdmApplication";
import { MatDialog } from "@angular/material/dialog";
import { UntypedFormControl, Validators } from "@angular/forms";
import { SecretServerService } from "src/app/services/secretServer/secretServer.service";
import { DirectoryDomain } from "src/app/models/directory/enums/DirectoryDomain.enum";
import { IGenericApiResponse } from "src/app/models/common/GenericApiResponse";


@Component({
    selector: 'app-defer-service-account',
    templateUrl: './defer-service-account.component.html',
    styleUrls: ['./defer-service-account.component.css']
})
export class DeferServiceAccountComponent implements OnInit {
    userType = UserType.Service;
    displayColumns: string[] = ['userPrincipalName', 'type', 'domain', 'applicationId', 'pasVaulted'];

    @ViewChild(MatPaginator, { static: false })
    set paginator(value: MatPaginator) {
        this.usersDataSource.paginator = value;
    }
    @ViewChild(MatSort, { static: false })
    set sort(value: MatSort) {
        this.usersDataSource.sort = value;
    }

    isRequestorChevronAppOwner: boolean = false;
    chevronApp: IIdmApplication | undefined = undefined;
    chevronAppOwnerDisplayNames: string[] = [];
    selectedDisplayCols: string[];
    PageLayout = PageLayout;
    errorMessage: string;
    gettingUsers: boolean = false;
    isSubmitting: boolean = false;
    submittedRequests: IWorkflowRequest[] = [];
    submitErrorMessage = { message: '', errors: [] };
    usersDataSource = new MatTableDataSource<IDirectoryUser>([]);
    selectedAccount: IDirectoryUser | undefined;
    displaySelectedAccount: any;
    serviceId: string | undefined;
    justification = new UntypedFormControl('', { validators: [Validators.required] });
    serviceAccountSelectErrorMessage: string = '';
    applicationDisplayName: string = '';
    domains: string[] = [DirectoryDomain.Chevron, DirectoryDomain.CT, DirectoryDomain.S02, DirectoryDomain.S01];

    constructor(
        private readonly userService: GraphUserService,
        private dialog: MatDialog,
        private readonly authService: CalAngularService,
        private readonly secretService: SecretServerService) {
    }

    ngOnInit(): void {
        this.selectedDisplayCols = [...this.displayColumns];
        this.usersDataSource.paginator = this.paginator
        this.usersDataSource.sort = this.sort;
    }

    resetSelectedAccount() {
        this.filter('');
        this.selectedAccount = undefined;
        this.displaySelectedAccount = undefined;
        this.serviceAccountSelectErrorMessage = '';
        this.justification.reset();
        this.submittedRequests = [];
        this.submitErrorMessage = { message: '', errors: [] };
    }

    public filter = (value: any = '') => {
        const filterValue = typeof value === 'string' ? value : value.target.value;
        this.usersDataSource.filter = filterValue.trim().toLocaleLowerCase();
    }

    selectRow(row: IDirectoryUser) {
        if (!row.pasVaulted) {
            this.serviceAccountSelectErrorMessage = 'Only service accounts that have been vaulted can be deferred - please consult <a href="https://go.chevron.com/ServiceAccountQRG" target="_blank">https://go.chevron.com/ServiceAccountQRG</a> to address this requirement';
            return;
        }
        this.displaySelectedAccount = {
            userPrincipalName: row.userPrincipalName,
            serviceId: row.applicationId,
            applicationName: this.applicationDisplayName,
            domain: row.domain
        };
        this.selectedAccount = row;
        this.serviceAccountSelectErrorMessage = '';
    }

    searchForChevronApplication(): void {
        const appDialogRef = this.dialog.open(DialogAppSearchChevronComponent, {
            disableClose: true,
            autoFocus: true,
            maxWidth: 1000,
            width: '100%',
            data: {
                includeEnvironments: false,
                includeApplicationOwners: true,
            } as IDialogAppSearchChevronData
        });

        appDialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.chevronApp = result as IIdmApplication;
                this.isRequestorChevronAppOwner = this.chevronApp.owners.some(owner => owner.id === this.authService.cvxClaimsPrincipal.objectId);
                this.chevronAppOwnerDisplayNames = this.chevronApp.owners.map(owner => owner.displayName) ?? [];
                this.serviceId = this.chevronApp.serviceId;
                this.applicationDisplayName = this.chevronApp.displayName;
                this.search();
            }
        });
    }
    async search() {
        this.gettingUsers = true;
        this.usersDataSource.data = [];
        this.errorMessage = '';

        const term: string = "";
        let enabledAccountsOnly: boolean = true;

        try {
            // Create an array of promises for all domain requests
            const domainPromises = this.domains.map(domain =>
                firstValueFrom(this.userService.SearchUser(
                    domain,
                    term,
                    this.userType,
                    undefined,
                    ResultSizeConstants.GetAll,
                    enabledAccountsOnly,
                    this.serviceId
                ))
            );

            // Wait for all domain requests to complete
            const results = await Promise.all(domainPromises);

            // Combine all results
            this.usersDataSource.data = results
                .filter((result: IGenericApiResponse<IDirectoryUser[]>) => result?.data !== undefined)
                .flatMap(result => result.data);

            this.errorMessage = this.usersDataSource.data.length === 0 ?
                'No service accounts were found.' : '';

        } catch (error) {
            console.error(error);
            this.errorMessage = 'Encountered unexpected error while searching for service accounts.';
        } finally {
            this.gettingUsers = false;
        }
    }


    onSubmit() {
        if (this.selectedAccount) {
            this.isSubmitting = true;
            let request: DeferServiceAccountRequest = {
                serviceAccount: {
                    userId: this.selectedAccount.id,
                    stewardUserId: this.authService.cvxClaimsPrincipal.objectId,
                    applicationId: this.selectedAccount.applicationId,
                    emailId: this.selectedAccount.userPrincipalName
                },
                justification: this.justification.value
            };

            this.userService.DeferServiceAccount(this.selectedAccount.domain, request).subscribe({
                next: (x: IGenericApiResponseWithWorkflowRequest<any>) => {
                    this.submittedRequests = x.requests;
                    this.submitErrorMessage = { message: '', errors: [] };
                },
                error: (err: any) => {
                    this.submitErrorMessage.message = err.statusText;
                    this.submitErrorMessage.errors = err?.error?.errors ?? [];
                    this.isSubmitting = false;
                },
                complete: () => {
                    this.isSubmitting = false;
                }
            });
        }
    }

    isArrayWithElements(value: any): boolean {
        return Array.isArray(value) && value.length > 0;
    }

    resetForm() {
        this.filter('');
        this.chevronAppOwnerDisplayNames = [];
        this.chevronApp = undefined;
        this.selectedAccount = undefined;
        this.displaySelectedAccount = undefined;
        this.submitErrorMessage = { message: '', errors: [] };
        this.serviceId = undefined;
        this.justification.reset();
        this.serviceAccountSelectErrorMessage = '';
        this.usersDataSource.data = [];
    }
}


